import React, { useState, useEffect, useMemo } from 'react';
import { useUser } from '../../context/UserContext';
import { useData } from '../../context/DataContext';
import { coursesService, sessionRegistrationsService } from '../../services/firestoreService';
import { analyticsService } from '../../services/analyticsService';
import { activityLogService } from '../../services/activityLogService';
import './AvailableSessions.css';
import SessionCard from '../shared/SessionCard';

const AvailableSessions = () => {
  const { user } = useUser();
  const { sessions: allSessions, loading: dataLoading } = useData();
  const [sessionsWithCourses, setSessionsWithCourses] = useState([]);
  const [registrationsMap, setRegistrationsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState({});
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'upcoming'
  const [pageViews, setPageViews] = useState(0);

  const normalizeDateValue = (value) => {
    if (!value) return null;
    if (typeof value === 'string') {
      if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
      }
      const parsed = new Date(value);
      return isNaN(parsed.getTime()) ? null : parsed;
    }
    if (value.toDate) return value.toDate();
    if (value.seconds) return new Date(value.seconds * 1000);
    const date = value instanceof Date ? value : new Date(value);
    return isNaN(date.getTime()) ? null : date;
  };

  const formatDateDisplay = (value) => {
    const date = normalizeDateValue(value);
    return date ? date.toLocaleDateString() : 'TBD';
  };

  const buildSessionMeta = (session) => {
    const meta = [];
    if (session.startDate) {
      meta.push({ icon: '📅', label: 'Start', value: formatDateDisplay(session.startDate) });
    }
    if (session.endDate) {
      meta.push({ icon: '🏁', label: 'End', value: formatDateDisplay(session.endDate) });
    }
    if (session.totalDuration) {
      meta.push({ icon: '⏱️', label: 'Duration', value: `${session.totalDuration} hrs` });
    }
    if (session.sessionCost != null && session.sessionCost !== '') {
      const costVal = typeof session.sessionCost === 'number' ? `$${session.sessionCost}` : session.sessionCost;
      meta.push({ icon: '💰', label: 'Cost', value: costVal });
    }
    if (session.maxStudents) {
      meta.push({ icon: '👥', label: 'Capacity', value: session.maxStudents });
    }
    return meta;
  };

  const buildTopicEntries = (session) => {
    if (!session.course?.topics?.length) return [];
    return session.course.topics.map((topic, idx) => ({
      id: `${session.id}-${idx}`,
      name: topic.name,
      hours: topic.hours ? `${topic.hours}h` : undefined,
      description: topic.description
    }));
  };

  // Track page view and subscribe to count
  useEffect(() => {
    if (!user) return; // Only track when user is logged in
    
    // Track the page view
    analyticsService.trackPageView('sessions-logged-in');

    // Subscribe to page view count updates
    const unsubscribe = analyticsService.subscribeToPageViewCount('sessions-logged-in', (count) => {
      setPageViews(count);
    });

    return () => unsubscribe();
  }, [user]);

  useEffect(() => {
    const loadSessions = async () => {
      try {
        // Fetch all sessions and enrich with course data
        const enrichedSessions = await Promise.all(
          allSessions.map(async (session) => {
            let course = null;
            if (session.courseId) {
              course = await coursesService.getCourse(session.courseId);
            }
            return {
              ...session,
              course
            };
          })
        );
        setSessionsWithCourses(enrichedSessions);

        // Load student registrations if logged in
        if (user && user.type === 'student') {
          const registrations = await sessionRegistrationsService.getStudentRegistrations(user.id);
          const regMap = {};
          registrations.forEach(reg => {
            regMap[reg.sessionId] = reg;
          });
          setRegistrationsMap(regMap);
        }
      } catch (error) {
        console.error('Error loading sessions:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!dataLoading) {
      loadSessions();
    }
  }, [allSessions, dataLoading, user]);

  // Filter sessions based on current date - only show active and future
  const filteredSessions = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return sessionsWithCourses.filter((session) => {
      const startDate = normalizeDateValue(session.startDate);
      const endDate = normalizeDateValue(session.endDate);

      if (!startDate || !endDate) return false;

      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      const isActive = startDate <= today && endDate >= today;
      const isUpcoming = startDate > today;

      switch (filter) {
        case 'active':
          return isActive;
        case 'upcoming':
          return isUpcoming;
        case 'all':
        default:
          return isActive || isUpcoming; // Show active and future only
      }
    }).sort((a, b) => {
      const aStart = normalizeDateValue(a.startDate) || 0;
      const bStart = normalizeDateValue(b.startDate) || 0;
      return aStart - bStart;
    });
  }, [sessionsWithCourses, filter]);

  const handleRegister = async (sessionId) => {
    if (!user || user.type !== 'student') {
      alert('Please sign in to register for sessions.');
      return;
    }

    setRegistering((prev) => ({ ...prev, [sessionId]: true }));
    try {
      await sessionRegistrationsService.registerForSession(sessionId, user.id);
      // Log registration activity
      await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.REGISTER_SESSION, {
        sessionId,
        status: 'pending'
      });
      // Update local registrations map
      setRegistrationsMap((prev) => ({
        ...prev,
        [sessionId]: {
          sessionId,
          studentId: user.id,
          status: 'pending',
          registeredAt: new Date()
        }
      }));
      alert('Successfully registered! Your registration is pending approval.');
    } catch (error) {
      console.error('❌ Error registering for session:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);
      console.error('User ID:', user?.id);
      console.error('Session ID:', sessionId);
      
      if (error.message.includes('Already registered')) {
        alert('You are already registered for this session.');
      } else if (error.message.includes('Session is full')) {
        alert(error.message);
      } else if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        console.error('⚠️ PERMISSION ERROR - Check Firestore security rules!');
        alert('Permission error. Please make sure you are signed in and try again. If the problem persists, contact support.');
      } else {
        alert(`Error registering for session: ${error.message || 'Please try again.'}`);
      }
    } finally {
      setRegistering((prev) => ({ ...prev, [sessionId]: false }));
    }
  };

  const getSessionStatus = (session) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const startDate = normalizeDateValue(session.startDate);
    const endDate = normalizeDateValue(session.endDate);

    if (!startDate || !endDate) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    }

    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (startDate <= today && endDate >= today) {
      return { label: '🟢 Active', type: 'active' };
    } else if (startDate > today) {
      return { label: '🔵 Upcoming', type: 'upcoming' };
    } else {
      return { label: '⚫ Past', type: 'past' };
    }
  };

  const getRegistrationStatus = (sessionId) => {
    const registration = registrationsMap[sessionId];
    if (!registration) return null;
    
    switch (registration.status) {
      case 'pending':
        return { label: '⏳ Registration Pending', type: 'pending' };
      case 'active':
        return { label: '✅ Registered', type: 'active' };
      case 'cancelled':
        return { label: '❌ Cancelled', type: 'past' };
      default:
        return null;
    }
  };

  if (loading || dataLoading) {
    return (
      <div className="available-sessions">
        <div className="empty-state">
          <p>Loading available sessions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="available-sessions">
      <div className="sessions-header">
        <h1>📅 Available Sessions</h1>
        {user && user.type === 'admin' && (
          <div className="page-views">👁️ {pageViews.toLocaleString()} views</div>
        )}
      </div>
      <div className="page-header">
        <div className="filter-buttons">
          <button
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All Available
          </button>
          <button
            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
            onClick={() => setFilter('active')}
          >
            🟢 Active
          </button>
          <button
            className={`filter-btn ${filter === 'upcoming' ? 'active' : ''}`}
            onClick={() => setFilter('upcoming')}
          >
            🔵 Upcoming
          </button>
        </div>
      </div>

      {filteredSessions.length === 0 ? (
        <div className="empty-state">
          <p>No {filter === 'all' ? 'available' : filter} sessions found.</p>
        </div>
      ) : (
        <div className="sessions-grid">
          {filteredSessions.map((session) => {
            const status = getSessionStatus(session);
            const regStatus = getRegistrationStatus(session.id);
            const isRegistered = !!registrationsMap[session.id];
            const isPending = registrationsMap[session.id]?.status === 'pending';
            const isActive = registrationsMap[session.id]?.status === 'active';

            const courseSection = session.course
              ? {
                  title: `📚 ${session.course.title}`,
                  description: session.course.description,
                  objectives: session.courseObjects,
                  expectations: session.expectations
                }
              : undefined;

            const registrationActions = user && user.type === 'student'
              ? (
                  <div className="registration-actions">
                    {!isRegistered ? (
                      <button
                        onClick={() => handleRegister(session.id)}
                        disabled={registering[session.id]}
                        className="register-btn"
                      >
                        {registering[session.id] ? 'Registering...' : '📝 Register for Session'}
                      </button>
                    ) : isPending ? (
                      <div className="registration-message">
                        <p>⏳ Your registration is pending approval.</p>
                      </div>
                    ) : isActive ? (
                      <div className="registration-message">
                        <p>✅ You are registered for this session.</p>
                        <p className="view-link">View in <strong>Sessions Registered</strong></p>
                      </div>
                    ) : null}
                  </div>
                )
              : null;

            return (
              <SessionCard
                key={session.id}
                title={session.title}
                status={status}
                secondaryStatus={regStatus}
                meta={buildSessionMeta(session)}
                course={courseSection}
                topics={buildTopicEntries(session)}
                actions={registrationActions}
              >
                {session.feesTerms && (
                  <div className="fees-terms">
                    <strong>💰 Fees/Terms:</strong>
                    <p>{session.feesTerms}</p>
                  </div>
                )}
              </SessionCard>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AvailableSessions;
